import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { logoutProcessor } from "../components/helpers/logoutProcessor";
import { useTranslation } from "../i18n";
import AdminPageHeader from "../components/admin-page-header";

import "../styles/zone-storage-settings.css";

import ZoneStorageSettingsBatchData from "../components/zone-storage-settings-batch-data";
import ZoneStorageSettingsChangeConfirmation from "../components/zone-storage-settings-change-confirmation";
import ZoneStorageSettingsLanes from "../components/zone-storage-settings-lanes";
import ZoneStorageSettingsSearch from "../components/zone-storage-settings-search";
import ZoneStorageSettingsBays from "../components/zone-storage-settings-bays";
import { apiFetch } from "../api/apiFetch";
import { useReadOnly } from "../access/ReadOnly";

type palletType = {
    Zone: string;
    PalletId: string;
    Collected: boolean;
}

type batchDataType = {
    batch: number;
    totalItems: number;
    totalScannedItems: number;
    totalPallets?: number;
    collectedPallets?: number;
    pallets?: palletType[];
}[]

type Setting = {
    _id: string,
    Setting: string,
    Value: any,
    Warehouse: string
}

const AdminZoneStorageSettings = () => {
    const navigator = useNavigate();
    const { t } = useTranslation();
    const readOnly = useReadOnly();

    const [warehouse, set_warehouse] = useState<"Newport" | "Poland" | "Test">("Newport");
    const [sorter1ActiveBatch, set_sorter1ActiveBatch] = useState<number>(0);
    const [sorter1ActiveDate, set_sorter1ActiveDate] = useState<string>("");
    const [sorter2ActiveBatch, set_sorter2ActiveBatch] = useState<number>(0);
    const [sorter2ActiveDate, set_sorter2ActiveDate] = useState<string>("");
    const [sorter2ChangeDate, set_sorter2ChangeDate] = useState<string>("");
    const [sorter2ChangeBatch, set_sorter2ChangeBatch] = useState<number>(0);
    const [displaySettings, set_displaySettings] = useState<{ sorter1Date: string, sorter1Batch: number, sorter2Date: string, sorter2Batch: number }>({ sorter1Date: "", sorter1Batch: 0, sorter2Date: "", sorter2Batch: 0 });
    const [selectedSorter1Date, set_selectedSorter1Date] = useState<string>("");
    const [selectedSorter2Date, set_selectedSorter2Date] = useState<string>("");
    const [selectedSorter2Batch, set_selectedSorter2Batch] = useState<number>(0);
    const [sorter1BatchData, set_sorter1BatchData] = useState<batchDataType>([{ batch: 0, totalItems: 0, totalScannedItems: 0 }]);
    const [sorter2BatchData, set_sorter2BatchData] = useState<batchDataType>([{ batch: 0, totalItems: 0, totalScannedItems: 0 }]);
    const [settingsPopupActive, set_settingsPopupActive] = useState<boolean>(false);

    useEffect(() => {
        const init = async () => {
            await apiFetch(`/zonestorage/settings/get-all`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ warehouse: warehouse })
            }).then(async (res): Promise<any> => {
                let response = await res.json();
                if (res.ok) {
                    let sorter1AB = 0, sorter1AD = "", sorter2AB = 0, sorter2AD = "";
                    response.forEach((settingObj: Setting) => {
                        if (settingObj.Setting.includes("Sorter 1 Active Batch")) sorter1AB = settingObj.Value;
                        else if (settingObj.Setting.includes("Sorter 1 Active Date")) sorter1AD = settingObj.Value.slice(0, 10);
                        else if (settingObj.Setting.includes("Sorter 2 Active Batch")) sorter2AB = settingObj.Value;
                        else if (settingObj.Setting.includes("Sorter 2 Active Date")) sorter2AD = settingObj.Value.slice(0, 10);
                    });
                    set_displaySettings({ sorter1Date: sorter1AD, sorter1Batch: sorter1AB, sorter2Date: sorter2AD, sorter2Batch: sorter2AB });
                    set_sorter1ActiveBatch(sorter1AB);
                    set_sorter1ActiveDate(sorter1AD);
                    set_sorter2ActiveBatch(sorter2AB);
                    set_sorter2ActiveDate(sorter2AD);
                    set_sorter2ChangeBatch(sorter2AB);
                    set_sorter2ChangeDate(sorter2AD);
                    set_selectedSorter1Date(() => { loadBatch(sorter1AD, 1); return sorter1AD; });
                    set_selectedSorter2Date(() => { loadBatch(sorter2AD, 2); return sorter2AD; });
                } else if (res.status === 401) {
                    logoutProcessor();
                    navigator("/login");
                } else {
                    toast.error(response.message);
                }
            });
        };
        init();
    }, [warehouse]);

    const loadBatch = async (date: string, sorter: number) => {
        const setDataFunc = sorter === 1 ? set_sorter1BatchData : set_sorter2BatchData;
        setDataFunc([]);
        await apiFetch(`/zonestorage/settings/batch-data`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ warehouse, day: date, sorter })
        }).then(async (res): Promise<any> => {
            let response = await res.json();
            if (res.ok) {
                setDataFunc(response);
                if (sorter === 1) loadPalletTallies(date, response, setDataFunc);
            }
            else if (res.status === 401) { logoutProcessor(); navigator("/login"); }
            else toast.error(response.message);
        });
    };

    // Pallets only exist for sorter 1 (they queue up ahead of it), so this
    // is only called for the sorter 1 batch data. Failures leave the tally
    // off the card rather than surfacing a toast per batch.
    const loadPalletTallies = async (date: string, batchData: batchDataType, setDataFunc: (data: batchDataType) => void) => {
        const withPallets = await Promise.all(batchData.map(async (data) => {
            const res = await apiFetch(`/zonestorage/pallets/get-batch-pallets`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ warehouse, date, batch: data.batch })
            });
            if (!res.ok) return data;
            const pallets: palletType[] = await res.json();
            // Sort pallets by collected status (uncollected first) and then by PalletId for consistency.
            pallets.sort((a, b) => {
                if (a.Collected !== b.Collected) return a.Collected ? 1 : -1;
                return a.PalletId.localeCompare(b.PalletId);
            });
            return { ...data, totalPallets: pallets.length, collectedPallets: pallets.filter(p => p.Collected).length, pallets };
        }));
        setDataFunc(withPallets);
    };

    const change_selected_date = (date: string, sorter: number) => {
        (sorter === 1 ? set_selectedSorter1Date : set_selectedSorter2Date)(date);
        loadBatch(date, sorter);
    };

    const handle_settings_change = async (sorter: number) => {
        const dataToSend = sorter === 1
            ? { warehouse, sorter: 1, newDate: sorter1ActiveDate, newBatch: sorter1ActiveBatch }
            : { warehouse, sorter: 2, newDate: sorter2ChangeDate, newBatch: sorter2ChangeBatch };
        await apiFetch(`/zonestorage/settings/set-all`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(dataToSend)
        }).then(async (res): Promise<any> => {
            let response = await res.json();
            if (res.ok) {
                set_displaySettings({ sorter1Date: sorter1ActiveDate, sorter1Batch: sorter1ActiveBatch, sorter2Date: sorter2ActiveDate, sorter2Batch: sorter2ActiveBatch });
                toast.success(t("success"));
            } else if (res.status === 401) { logoutProcessor(); navigator("/login"); }
            else toast.error(response.error);
        });
        if (sorter === 2) {
            set_sorter2ActiveBatch(sorter2ChangeBatch);
            set_sorter2ActiveDate(sorter2ChangeDate);
        }
    };

    const changed_sorter_one_in_popup = (newDate: string, newBatch: number) => {
        set_displaySettings({ sorter1Date: newDate, sorter1Batch: newBatch, sorter2Batch: displaySettings.sorter2Batch, sorter2Date: displaySettings.sorter2Date });
        set_sorter1ActiveBatch(newBatch);
        set_sorter1ActiveDate(newDate);
        set_settingsPopupActive(false);
    };

    return (
        <div className="page-data page-data-wide zs-settings-page">
            <AdminPageHeader
                titleKey="zone_storage_settings"
                backTo="/admin"
                hideSearch
                crumbs={[{ labelKey: "admin_panel", to: "/admin" }, { labelKey: "zone_storage" }]}
            />

            {/* Warehouse pill row — uses the shared .filter-pill styling
                so the chrome matches the rest of the admin section. This
                page is single-select (you can only view one site's batches
                at a time), so we render the pills directly rather than
                routing through the FilterPills component which always
                surfaces an "All" pill. */}
            <div className="filter-pills" role="tablist">
                {(["Newport", "Poland", "Test"] as const).map(w => (
                    <button
                        key={w}
                        type="button"
                        role="tab"
                        aria-selected={warehouse === w}
                        className={`filter-pill${warehouse === w ? " filter-pill--active" : ""}`}
                        onClick={() => set_warehouse(w)}
                    >
                        {t(w.toLowerCase() as any)}
                    </button>
                ))}
            </div>

            {/* Current settings — compact card */}
            <div className="zs-card">
                <h2 className="zs-card-title">{t("current_settings")}</h2>
                <div className="zs-settings-row">
                    <div className="zs-setting-item">
                        <span className="zs-setting-label">{t("sorter_1")}</span>
                        <span className="zs-setting-value">{t("batch_label", { batch: String(displaySettings.sorter1Batch) })}</span>
                        <span className="zs-setting-sub">{displaySettings.sorter1Date}</span>
                    </div>
                    <div className="zs-setting-item">
                        <span className="zs-setting-label">{t("sorter_2")}</span>
                        <span className="zs-setting-value">{t("batch_label", { batch: String(displaySettings.sorter2Batch) })}</span>
                        <span className="zs-setting-sub">{displaySettings.sorter2Date}</span>
                    </div>
                </div>
            </div>

            {/* Change settings — side by side */}
            <div className="zs-card">
                <h2 className="zs-card-title">{t("change_settings")}</h2>
                <div className="zs-change-row">
                    <div className="zs-change-col">
                        <h3 className="zs-change-label">{t("sorter_1")}</h3>
                        <button
                            className="btn btn-secondary btn-full"
                            onClick={() => set_settingsPopupActive(true)}
                            disabled={readOnly}
                            aria-disabled={readOnly}
                            title={readOnly ? t("read_only_mode") : undefined}
                        >
                            {t("change_settings")}
                        </button>
                    </div>
                    <div className="zs-change-col">
                        <h3 className="zs-change-label">{t("sorter_2")}</h3>
                        <div className="zs-change-form">
                            <label className="zs-input-label">{t("change_batch")}</label>
                            <input type="number" className="zs-input" value={sorter2ChangeBatch} onChange={(e) => set_sorter2ChangeBatch(Number(e.target.value))} disabled={readOnly} />
                            <label className="zs-input-label">{t("change_date")}</label>
                            <input type="date" className="zs-input" value={sorter2ChangeDate} onChange={(e) => set_sorter2ChangeDate(e.target.value)} disabled={readOnly} />
                            <button
                                className="btn btn-success btn-full"
                                onClick={() => handle_settings_change(2)}
                                disabled={readOnly}
                                aria-disabled={readOnly}
                                title={readOnly ? t("read_only_mode") : undefined}
                            >
                                {t("set_new_values")}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Batch data */}
            <ZoneStorageSettingsBatchData sorter={1} activeBatch={sorter1ActiveBatch} activeDate={sorter1ActiveDate} selectedDate={selectedSorter1Date} batchData={sorter1BatchData} changeDateFunc={change_selected_date} reloadBatch={loadBatch} />
            <ZoneStorageSettingsBatchData sorter={2} activeBatch={sorter2ActiveBatch} activeDate={sorter2ActiveDate} selectedDate={selectedSorter2Date} batchData={sorter2BatchData} changeDateFunc={change_selected_date} reloadBatch={loadBatch} />

            <ZoneStorageSettingsBays warehouse={warehouse} />
            <ZoneStorageSettingsLanes warehouse={warehouse} selectedDate={selectedSorter2Date} changeDateFunc={change_selected_date} />
            <ZoneStorageSettingsSearch warehouse={warehouse} />

            {settingsPopupActive && <ZoneStorageSettingsChangeConfirmation warehouse={warehouse} activeBatch={sorter1ActiveBatch} set_settingsPopupActive={set_settingsPopupActive} changeSorterOnePopup={changed_sorter_one_in_popup} />}
        </div>
    );
};

export default AdminZoneStorageSettings;
